<script setup lang="ts">
import { computed } from 'vue';
import { parseISO } from 'date-fns';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { lotFraction, lotTitle, countPercent, formatPercent, formatQuantity, PERCENT_TONE_CLASSES, PERCENT_TONE_TEXT_CLASSES, percentTone, signedNumber, signTone } from '@/composables/inventory/inventoryTable';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { i18n } from '@/plugins/internationalization/i18n';
import type { MedicineInventoryRow } from '@/types';

const props = defineProps<{ detail: MedicineInventoryRow }>();

const { periodRange } = useInventoryScope();

const label = (key: string) => i18n.global.t(`views.health.inventory.table.headings.${key}`);
const formatDate = (date: string) => i18n.global.d(parseISO(date), 'short');

/** Nothing adjusted reads quiet; a shortfall found at a count is red, a surplus green. */
function adjustmentTone(adjustment: number): string {
    return adjustment === 0 ? 'text-gray-400' : signTone(adjustment);
}

/** The period's figures, one card each; the balance closes the row. */
const figures = computed(() => {
    const { opening, bought, used, wasted, adjustment } = props.detail;

    return [
        { id: 'opening', label: label('opening'), value: formatQuantity(opening), tone: '' },
        { id: 'bought', label: label('bought'), value: formatQuantity(bought), tone: '' },
        { id: 'used', label: label('used'), value: formatQuantity(used), tone: '' },
        { id: 'wasted', label: label('wasted'), value: formatQuantity(wasted), tone: '' },
        { id: 'adjustment', label: label('adjustment'), value: signedNumber(adjustment, formatQuantity), tone: adjustmentTone(adjustment) },
    ];
});

const lastCountPercent = computed(() => countPercent(props.detail.last_count));

/** What the last count was off by, in the key's unit; "matches" when it found what was expected. */
const lastCountDifference = computed(() => {
    const { last_count: lastCount, unit_name: unitName } = props.detail;

    if (lastCount === null) {
        return '';
    }

    return lastCount.difference === 0 ? i18n.global.t('views.health.inventory.chips.countMatches') : `${signedNumber(lastCount.difference, formatQuantity)} ${unitName}`;
});

const period = computed(() => {
    const { from, to } = periodRange.value;

    return from && to ? i18n.global.t('views.health.inventory.detail.period', { from: formatDate(from), to: formatDate(to) }) : '';
});
</script>

<template>
    <div class="space-y-2">
        <div v-if="period" class="text-normal text-gray-500">{{ period }}</div>
        <!--
            One card, seven cells: a single row from lg up, divided by thin rules, so no width goes to gaps
            between cards. Narrower screens fall back to a two- or four-column grid with row rules instead.
        -->
        <MainViewCard class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-gray-100 max-lg:gap-y-1 lg:divide-x">
            <div v-for="figure in figures" :key="figure.id" class="min-w-0 px-4 py-3">
                <div class="truncate text-sm text-gray-500">{{ figure.label }}</div>
                <div class="text-xl font-semibold whitespace-nowrap tabular-nums" :class="figure.tone">
                    {{ figure.value }}<small class="ml-1 text-xs font-normal text-gray-500">{{ detail.unit_name }}</small>
                </div>
            </div>
            <div class="min-w-0 px-4 py-3" :title="lotTitle(detail)">
                <div class="truncate text-sm text-gray-500">{{ label('closing') }}</div>
                <div class="text-xl font-bold whitespace-nowrap tabular-nums" :class="{ 'text-red-600': detail.closing < 0 }">
                    {{ formatQuantity(detail.closing) }}<small class="ml-1 text-xs font-normal text-gray-500">{{ detail.unit_name }}</small>
                </div>
                <Badge v-if="!detail.has_purchase" variant="outline" class="mt-1 whitespace-nowrap font-medium" :class="PERCENT_TONE_CLASSES.error">
                    {{ $t('views.health.inventory.chips.consumptionWithoutPurchase') }}
                </Badge>
                <div v-else class="mt-1.5 h-1.5 w-16 overflow-hidden rounded bg-gray-200">
                    <div class="h-full rounded bg-primary-700" :style="{ width: `${detail.active_lot ? lotFraction(detail.active_lot) * 100 : 0}%` }"></div>
                </div>
            </div>
            <div class="min-w-0 px-4 py-3">
                <div class="truncate text-sm text-gray-500">{{ label('lastCount') }}</div>
                <!-- The share the count was off by, with the amount beside it in brackets; the date below, a step larger than a caption. -->
                <template v-if="detail.last_count">
                    <div class="flex flex-wrap items-baseline gap-x-1">
                        <span v-if="lastCountPercent !== null" class="text-xl font-semibold whitespace-nowrap tabular-nums" :class="PERCENT_TONE_TEXT_CLASSES[percentTone(lastCountPercent)]">
                            {{ formatPercent(lastCountPercent) }}
                        </span>
                        <span v-else class="text-xl font-semibold text-gray-400" aria-hidden="true">&ndash;</span>
                        <span class="whitespace-nowrap text-xs text-gray-500 tabular-nums">({{ lastCountDifference }})</span>
                    </div>
                    <div class="whitespace-nowrap text-sm text-gray-700">{{ formatDate(detail.last_count.date) }}</div>
                </template>
                <div v-else class="text-xl font-semibold text-gray-400">{{ $t('views.health.inventory.chips.noCount') }}</div>
            </div>
        </MainViewCard>
    </div>
</template>
